import { domainsService } from '@/services/domains.service';
import { useDomainsStore } from '@/store/domains.store';
import { useOrganisationsStore } from '@/store/organisations.store';
import {
  DomainsQueryParams,
  FoldersQueryParams,
  GlobalFoldersQueryParams,
} from '@/types/domain.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { toast } from 'sonner';

type UpdateDomainData = Parameters<typeof domainsService.updateDomain>[1];

// Query Keys
export const domainKeys = {
  all: ['domains'] as const,
  list: (params: DomainsQueryParams) => ['domains', 'list', params] as const,
  detail: (uuid: string) => ['domains', uuid] as const,
  folders: (domainUuid: string, params: FoldersQueryParams) =>
    ['domains', domainUuid, 'folders', params] as const,
  globalFolders: (params: GlobalFoldersQueryParams) =>
    ['domains', 'folders', 'global', params] as const,
};

// Hook for domains list with pagination and search
export const useDomains = () => {
  const {
    getQueryParams,
    setDomains,
    setPagination,
    setLoadingDomains,
    setError,
    clearError,
  } = useDomainsStore();
  const { currentOrganisation } = useOrganisationsStore();

  const queryParams = getQueryParams();

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: domainKeys.list({
      ...queryParams,
      organisationUuid: currentOrganisation?.uuid,
    }),
    queryFn: () => domainsService.getDomains(queryParams),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Handle success and error with useEffect
  useEffect(() => {
    if (data) {
      setDomains(data.data);
      clearError();

      // Update pagination if available
      if (data.pagination) {
        setPagination({
          currentPage: data.pagination.page,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.total,
          hasNext: data.pagination.hasNext,
          hasPrev: data.pagination.hasPrev,
        });
      }
    }
  }, [data, setDomains, setPagination, clearError]);

  useEffect(() => {
    if (error) {
      const message =
        (error as HttpApiError)?.response?.data?.message ||
        (error as HttpApiError)?.message ||
        'Failed to load domains';
      setError(message);
    }
  }, [error, setError]);

  // Update loading state
  useEffect(() => {
    setLoadingDomains(isLoading || isFetching);
  }, [isLoading, isFetching, setLoadingDomains]);

  return {
    domains: data?.data || [],
    isLoading: isLoading || isFetching,
    error,
    refetch,
    pagination: data?.pagination,
  };
};

// Hook for syncing domains from OpenPaye
export const useSyncDomainsFromOpenPaye = () => {
  const queryClient = useQueryClient();
  const { setSyncing, setError, clearError } = useDomainsStore();

  return useMutation({
    mutationFn: () => domainsService.syncDomainsFromOpenPaye(),
    onMutate: () => {
      setSyncing(true);
      clearError();
    },
    onSuccess: response => {
      toast.success(
        response.message || 'La liste des domaines et dossiers a été actualisée'
      );

      // Invalidate and refetch domains
      queryClient.invalidateQueries({ queryKey: domainKeys.all });
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Impossible d'actualiser les domaines et dossiers";
      setError(message);
      toast.error(message);
    },
    onSettled: () => {
      setSyncing(false);
    },
  });
};

// Hook for single domain sync
export const useSyncDomain = () => {
  const queryClient = useQueryClient();
  const { setSyncingDomainId, updateDomain, setError, clearError } =
    useDomainsStore();

  return useMutation({
    mutationFn: (uuid: string) => domainsService.syncDomain(uuid),
    onMutate: uuid => {
      setSyncingDomainId(uuid);
      clearError();

      // Optimistically update domain status
      updateDomain(uuid, { status: 'syncing' });
    },
    onSuccess: (response, uuid) => {
      toast.success(
        response.message || 'La liste des dossiers a été actualisée'
      );

      // Update domain status
      updateDomain(uuid, {
        status: 'connected',
        lastSyncAt: new Date().toISOString(),
        lastSyncStatus: 'success',
      });

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: domainKeys.detail(uuid) });
      queryClient.invalidateQueries({ queryKey: domainKeys.all });
    },
    onError: (error: HttpApiError, uuid) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Impossible d'actualiser la liste des dossiers";

      setError(message);
      toast.error(message);

      // Update domain with error status
      updateDomain(uuid, {
        status: 'error',
        lastSyncStatus: 'error',
        lastSyncError: message,
      });
    },
    onSettled: () => {
      setSyncingDomainId(null);
    },
  });
};

// Hook for updating domain settings
export const useUpdateDomain = () => {
  const queryClient = useQueryClient();
  const { updateDomain, setError, clearError } = useDomainsStore();

  return useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: UpdateDomainData }) =>
      domainsService.updateDomain(uuid, data),
    onMutate: ({ uuid, data }) => {
      clearError();
      // Optimistically update domain
      updateDomain(uuid, data);
    },
    onSuccess: (_response, { uuid }) => {
      toast.success('Domain updated successfully');

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: domainKeys.detail(uuid) });
      queryClient.invalidateQueries({ queryKey: domainKeys.all });
    },
    onError: (error: HttpApiError, { uuid: _uuid, data: _data }) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update domain';

      setError(message);
      toast.error(message);

      // Revert optimistic update - you might want to refetch instead
      queryClient.invalidateQueries({ queryKey: domainKeys.all });
    },
  });
};

// Hook for domain search with debounce
export const useDomainsSearch = () => {
  const { setSearchQuery, setCurrentPage } = useDomainsStore();

  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      setCurrentPage(1); // Reset to first page when searching
    },
    [setSearchQuery, setCurrentPage]
  );

  return { handleSearch };
};

// Hook for domain filters
export const useDomainsFilters = () => {
  const {
    statusFilter,
    sortBy,
    sortOrder,
    setStatusFilter,
    setSortBy,
    setSortOrder,
    setCurrentPage,
  } = useDomainsStore();

  const handleStatusFilter = useCallback(
    (status: typeof statusFilter) => {
      setStatusFilter(status);
      setCurrentPage(1);
    },
    [setStatusFilter, setCurrentPage]
  );

  const handleSort = useCallback(
    (field: typeof sortBy, order: typeof sortOrder) => {
      setSortBy(field);
      setSortOrder(order);
      setCurrentPage(1);
    },
    [setSortBy, setSortOrder, setCurrentPage]
  );

  return {
    statusFilter,
    sortBy,
    sortOrder,
    handleStatusFilter,
    handleSort,
  };
};

// Hook for pagination
export const useDomainsPagination = () => {
  const {
    currentPage,
    totalPages,
    totalItems,
    hasNext,
    hasPrev,
    setCurrentPage,
  } = useDomainsStore();

  const goToPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
      }
    },
    [totalPages, setCurrentPage]
  );

  const nextPage = useCallback(() => {
    if (hasNext) {
      setCurrentPage(currentPage + 1);
    }
  }, [hasNext, currentPage, setCurrentPage]);

  const prevPage = useCallback(() => {
    if (hasPrev) {
      setCurrentPage(currentPage - 1);
    }
  }, [hasPrev, currentPage, setCurrentPage]);

  return {
    currentPage,
    totalPages,
    totalItems,
    hasNext,
    hasPrev,
    goToPage,
    nextPage,
    prevPage,
  };
};

// Hook for domain detail
export const useDomainDetail = (domainUuid: string) => {
  const { setSelectedDomain, setLoadingDomainDetail, setError, clearError } =
    useDomainsStore();

  const { data, isLoading, error } = useQuery({
    queryKey: domainKeys.detail(domainUuid),
    queryFn: () => domainsService.getDomain(domainUuid),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!domainUuid,
  });

  useEffect(() => {
    if (data) {
      setSelectedDomain(data.data);
      clearError();
    }
  }, [data, setSelectedDomain, clearError]);

  useEffect(() => {
    if (error) {
      const message =
        (error as HttpApiError)?.response?.data?.message ||
        (error as HttpApiError)?.message ||
        'Failed to load domain details';
      setError(message);
    }
  }, [error, setError]);

  useEffect(() => {
    setLoadingDomainDetail(isLoading);
  }, [isLoading, setLoadingDomainDetail]);

  return {
    domain: data?.data || null,
    isLoading,
    error,
  };
};

// Hook for domain folders with pagination and search
export const useDomainFolders = (domainUuid: string) => {
  const {
    getFoldersQueryParams,
    setFolders,
    setFoldersPagination,
    setLoadingFolders,
    setError,
    clearError,
  } = useDomainsStore();

  const queryParams = getFoldersQueryParams();

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: domainKeys.folders(domainUuid, queryParams),
    queryFn: () => domainsService.getDomainFolders(domainUuid, queryParams),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!domainUuid,
  });

  // Handle success and error with useEffect
  useEffect(() => {
    if (data) {
      setFolders(data.data);
      clearError();

      // Update pagination
      setFoldersPagination({
        currentPage: data.page,
        totalPages: data.totalPages,
        totalItems: data.total,
        hasNext: data.hasNextPage,
        hasPrev: data.hasPreviousPage,
      });
    }
  }, [data, setFolders, setFoldersPagination, clearError]);

  useEffect(() => {
    if (error) {
      const message =
        (error as HttpApiError)?.response?.data?.message ||
        (error as HttpApiError)?.message ||
        'Failed to load folders';
      setError(message);
    }
  }, [error, setError]);

  // Update loading state
  useEffect(() => {
    setLoadingFolders(isLoading || isFetching);
  }, [isLoading, isFetching, setLoadingFolders]);

  return {
    folders: data?.data || [],
    isLoading: isLoading || isFetching,
    error,
    refetch,
    pagination: {
      page: data?.page || 1,
      limit: data?.limit || 15,
      total: data?.total || 0,
      totalPages: data?.totalPages || 1,
      hasNextPage: data?.hasNextPage || false,
      hasPreviousPage: data?.hasPreviousPage || false,
    },
  };
};

// Hook for folder search with debounce
export const useFoldersSearch = () => {
  const { setFoldersSearchQuery, setFoldersCurrentPage } = useDomainsStore();

  const handleSearch = useCallback(
    (query: string) => {
      setFoldersSearchQuery(query);
      setFoldersCurrentPage(1); // Reset to first page when searching
    },
    [setFoldersSearchQuery, setFoldersCurrentPage]
  );

  return { handleSearch };
};

// Hook for folder filters
export const useFoldersFilters = () => {
  const {
    foldersStatusFilter,
    foldersSortBy,
    foldersSortOrder,
    setFoldersStatusFilter,
    setFoldersSortBy,
    setFoldersSortOrder,
    setFoldersCurrentPage,
  } = useDomainsStore();

  const handleStatusFilter = useCallback(
    (status: typeof foldersStatusFilter) => {
      setFoldersStatusFilter(status);
      setFoldersCurrentPage(1);
    },
    [setFoldersStatusFilter, setFoldersCurrentPage]
  );

  const handleSort = useCallback(
    (field: typeof foldersSortBy, order: typeof foldersSortOrder) => {
      setFoldersSortBy(field);
      setFoldersSortOrder(order);
      setFoldersCurrentPage(1);
    },
    [setFoldersSortBy, setFoldersSortOrder, setFoldersCurrentPage]
  );

  return {
    foldersStatusFilter,
    foldersSortBy,
    foldersSortOrder,
    handleStatusFilter,
    handleSort,
  };
};

// Hook for folders pagination
export const useFoldersPagination = () => {
  const {
    foldersCurrentPage,
    foldersTotalPages,
    foldersTotalItems,
    foldersHasNext,
    foldersHasPrev,
    setFoldersCurrentPage,
  } = useDomainsStore();

  const goToPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= foldersTotalPages) {
        setFoldersCurrentPage(page);
      }
    },
    [foldersTotalPages, setFoldersCurrentPage]
  );

  const nextPage = useCallback(() => {
    if (foldersHasNext) {
      setFoldersCurrentPage(foldersCurrentPage + 1);
    }
  }, [foldersHasNext, foldersCurrentPage, setFoldersCurrentPage]);

  const prevPage = useCallback(() => {
    if (foldersHasPrev) {
      setFoldersCurrentPage(foldersCurrentPage - 1);
    }
  }, [foldersHasPrev, foldersCurrentPage, setFoldersCurrentPage]);

  return {
    currentPage: foldersCurrentPage,
    totalPages: foldersTotalPages,
    totalItems: foldersTotalItems,
    hasNext: foldersHasNext,
    hasPrev: foldersHasPrev,
    goToPage,
    nextPage,
    prevPage,
  };
};

// Hook for global folders with pagination and search
export const useGlobalFolders = () => {
  const {
    getGlobalFoldersQueryParams,
    setGlobalFolders,
    setGlobalFoldersPagination,
    setLoadingGlobalFolders,
    setError,
    clearError,
  } = useDomainsStore();

  const queryParams = getGlobalFoldersQueryParams();

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: domainKeys.globalFolders(queryParams),
    queryFn: () => domainsService.getAllFolders(queryParams),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Handle success and error with useEffect
  useEffect(() => {
    if (data) {
      setGlobalFolders(data.data);
      clearError();

      // Update pagination
      setGlobalFoldersPagination({
        currentPage: data.page,
        totalPages: data.totalPages,
        totalItems: data.total,
        hasNext: data.hasNextPage,
        hasPrev: data.hasPreviousPage,
      });
    }
  }, [data, setGlobalFolders, setGlobalFoldersPagination, clearError]);

  useEffect(() => {
    if (error) {
      const message =
        (error as HttpApiError)?.response?.data?.message ||
        (error as HttpApiError)?.message ||
        'Failed to load global folders';
      setError(message);
    }
  }, [error, setError]);

  // Update loading state
  useEffect(() => {
    setLoadingGlobalFolders(isLoading || isFetching);
  }, [isLoading, isFetching, setLoadingGlobalFolders]);

  return {
    folders: data?.data || [],
    isLoading: isLoading || isFetching,
    error,
    refetch,
    pagination: {
      page: data?.page || 1,
      limit: data?.limit || 15,
      total: data?.total || 0,
      totalPages: data?.totalPages || 1,
      hasNextPage: data?.hasNextPage || false,
      hasPreviousPage: data?.hasPreviousPage || false,
    },
  };
};

// Hook for global folder search with debounce
export const useGlobalFoldersSearch = () => {
  const { setGlobalFoldersSearchQuery, setGlobalFoldersCurrentPage } =
    useDomainsStore();

  const handleSearch = useCallback(
    (query: string) => {
      setGlobalFoldersSearchQuery(query);
      setGlobalFoldersCurrentPage(1); // Reset to first page when searching
    },
    [setGlobalFoldersSearchQuery, setGlobalFoldersCurrentPage]
  );

  return { handleSearch };
};

// Hook for global folder filters
export const useGlobalFoldersFilters = () => {
  const {
    globalFoldersStatusFilter,
    globalFoldersSyncStatusFilter,
    globalFoldersDomainNameFilter,
    globalFoldersSortBy,
    globalFoldersSortOrder,
    setGlobalFoldersStatusFilter,
    setGlobalFoldersSyncStatusFilter,
    setGlobalFoldersDomainNameFilter,
    setGlobalFoldersSortBy,
    setGlobalFoldersSortOrder,
    setGlobalFoldersCurrentPage,
  } = useDomainsStore();

  const handleStatusFilter = useCallback(
    (status: typeof globalFoldersStatusFilter) => {
      setGlobalFoldersStatusFilter(status);
      setGlobalFoldersCurrentPage(1);
    },
    [setGlobalFoldersStatusFilter, setGlobalFoldersCurrentPage]
  );

  const handleSyncStatusFilter = useCallback(
    (syncStatus: typeof globalFoldersSyncStatusFilter) => {
      setGlobalFoldersSyncStatusFilter(syncStatus);
      setGlobalFoldersCurrentPage(1);
    },
    [setGlobalFoldersSyncStatusFilter, setGlobalFoldersCurrentPage]
  );

  const handleDomainNameFilter = useCallback(
    (domainName: string) => {
      setGlobalFoldersDomainNameFilter(domainName);
      setGlobalFoldersCurrentPage(1);
    },
    [setGlobalFoldersDomainNameFilter, setGlobalFoldersCurrentPage]
  );

  const handleSort = useCallback(
    (
      field: typeof globalFoldersSortBy,
      order: typeof globalFoldersSortOrder
    ) => {
      setGlobalFoldersSortBy(field);
      setGlobalFoldersSortOrder(order);
      setGlobalFoldersCurrentPage(1);
    },
    [
      setGlobalFoldersSortBy,
      setGlobalFoldersSortOrder,
      setGlobalFoldersCurrentPage,
    ]
  );

  return {
    globalFoldersStatusFilter,
    globalFoldersSyncStatusFilter,
    globalFoldersDomainNameFilter,
    globalFoldersSortBy,
    globalFoldersSortOrder,
    handleStatusFilter,
    handleSyncStatusFilter,
    handleDomainNameFilter,
    handleSort,
  };
};

// Hook for global folders pagination
export const useGlobalFoldersPagination = () => {
  const {
    globalFoldersCurrentPage,
    globalFoldersTotalPages,
    globalFoldersTotalItems,
    globalFoldersHasNext,
    globalFoldersHasPrev,
    setGlobalFoldersCurrentPage,
  } = useDomainsStore();

  const goToPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= globalFoldersTotalPages) {
        setGlobalFoldersCurrentPage(page);
      }
    },
    [globalFoldersTotalPages, setGlobalFoldersCurrentPage]
  );

  const nextPage = useCallback(() => {
    if (globalFoldersHasNext) {
      setGlobalFoldersCurrentPage(globalFoldersCurrentPage + 1);
    }
  }, [
    globalFoldersHasNext,
    globalFoldersCurrentPage,
    setGlobalFoldersCurrentPage,
  ]);

  const prevPage = useCallback(() => {
    if (globalFoldersHasPrev) {
      setGlobalFoldersCurrentPage(globalFoldersCurrentPage - 1);
    }
  }, [
    globalFoldersHasPrev,
    globalFoldersCurrentPage,
    setGlobalFoldersCurrentPage,
  ]);

  return {
    currentPage: globalFoldersCurrentPage,
    totalPages: globalFoldersTotalPages,
    totalItems: globalFoldersTotalItems,
    hasNext: globalFoldersHasNext,
    hasPrev: globalFoldersHasPrev,
    goToPage,
    nextPage,
    prevPage,
  };
};

export const useFolderDetail = (folderUuid: string | undefined) => {
  return useQuery({
    queryKey: ['folder', 'detail', folderUuid],
    queryFn: () => domainsService.getFolderDetail(folderUuid!),
    enabled: !!folderUuid,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useFolderEstablishments = (folderUuid: string | undefined) => {
  return useQuery({
    queryKey: ['folder', 'establishments', folderUuid],
    queryFn: () =>
      domainsService.getFolderEstablishments(folderUuid!, {
        page: 1,
        limit: 10,
      }),
    enabled: !!folderUuid,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
